import { Component, Injectable, Input, OnInit, Inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { Enums, IEnums } from '../../app.enums';
import {
    AuthenticationService, LanguageService,
    LoggerService, ILogFn
} from '../../_shared/services/index';
import * as Api from '../../_shared/services/api/api-index';
import * as Entities from '../../_shared/entities/index';
import * as Utilities from '../../_shared/utilities/utilities-index';
import * as Models from '../../_shared/models/models-index';
import * as _ from 'underscore';
import { CustomValidators, MustMatch } from 'app/_shared/utilities/custom-validators';

@Component({
    selector: 'app-user-details-edit-modal',
    templateUrl: './userDetailsEdit.component.html',
    styleUrls: ['./userDetailsEdit.component.scss'],
    standalone: false
})
@Injectable()
export class UserDetailsEditComponent implements OnInit {
    @Input() user?: Entities.User;
    @Input() translationInfo: Models.ITranslationInfo;
    loading: boolean;
    userDetails: UntypedFormGroup;
    userName: UntypedFormControl;
    firstName: UntypedFormControl;
    lastName: UntypedFormControl;
    roles: UntypedFormControl;
    retailerId: UntypedFormControl;
    password: UntypedFormControl;
    confirmPassword: UntypedFormControl;
    changePasswordForm: UntypedFormGroup;
    changePassword: UntypedFormControl;
    confirmChangePassword: UntypedFormControl;
    isNew = false;
    roles$ = this.userService.getRoles();
    retailers$ = this.retailersService.fetchAll();
    enums: IEnums = Enums;
    hideCurrentPassword = false;
    errorMatch: Utilities.FormErrorStateMatcher;
    passwordMatcher: Utilities.CompareToErrorStateMatcher;
    userImage = 'assets/images/users/default-user.jpg';
    errorMessage: string;
    file = '';
    fileName = '';
    selectedFile: FormData;

    colors = [
        { value: 'gradient-purple', viewValue: 'Purple' },
        { value: 'gradient-indigo', viewValue: 'Indigo' },
        { value: 'gradient-teal', viewValue: 'Teal' },
        { value: 'gradient-blue', viewValue: 'Blue' },
        { value: 'gradient-orange', viewValue: 'Orange' },
        { value: 'gradient-green', viewValue: 'Green' },
        { value: 'gradient-pink', viewValue: 'Pink' },
        { value: 'gradient-red', viewValue: 'Red' },
        { value: 'gradient-amber', viewValue: 'Amber' },
        { value: 'gradient-gray', viewValue: 'Gray' },
        { value: 'gradient-brown', viewValue: 'Brown' },
        { value: 'gradient-lime', viewValue: 'Lime' },
        { value: 'gradient-coolblue', viewValue: 'Cool Blue' },
        { value: 'gradient-megatron', viewValue: 'Megatron' },
        { value: 'gradient-asteroid', viewValue: 'Asteroid' },
        { value: 'gradient-shine', viewValue: 'Shine' },
        { value: 'gradient-eveningshine', viewValue: 'Evening Shine' },
        { value: 'gradient-darkocean', viewValue: 'Dark Ocean' },
        { value: 'gradient-coolsky', viewValue: 'Cool Sky' },
        { value: 'gradient-yoda', viewValue: 'Yoda' },
        { value: 'gradient-memariani', viewValue: 'Memariani' },
        { value: 'gradient-amin', viewValue: 'Amin' },
        { value: 'gradient-harvey', viewValue: 'Harvey' },
        { value: 'gradient-neuromancer', viewValue: 'Neuromancer' },
        { value: 'gradient-azurelane', viewValue: 'Azure Lane' },
        { value: 'gradient-witchinghour', viewValue: 'Witching Hour' },
        { value: 'gradient-flare', viewValue: 'Flare' },
        { value: 'gradient-metapolis', viewValue: 'Metapolis' },
        { value: 'gradient-kpal', viewValue: 'Kpal' },
        { value: 'gradient-kmeh', viewValue: 'Kmeh' },
        { value: 'gradient-ktah', viewValue: 'Ktah' },
        { value: 'gradient-bydesign', viewValue: 'By Design' },
        { value: 'gradient-ultraviolet', viewValue: 'Ultraviolet' },
        { value: 'gradient-burningorange', viewValue: 'Burning Orange' },
        { value: 'gradient-wiretap', viewValue: 'Wiretap' },
        { value: 'gradient-summer', viewValue: 'Summer' },
        { value: 'gradient-racingred', viewValue: 'Racing Red' },
        { value: 'gradient-citrus', viewValue: 'Citrus' },
        { value: 'gradient-blueraspberry', viewValue: 'Blue Raspberry' },
        { value: 'gradient-magic', viewValue: 'Magic' }
    ];

    imageChangedEvent: any = '';
    croppedImage: any = '';
    showCropper = false;
    showCropperCard = false;
    tempImage = '';
    currentUser: Entities.User;

    private log: ILogFn;
    private logError: ILogFn;
    private logSuccess: ILogFn;

    constructor(
        public activeModal: MatDialogRef<UserDetailsEditComponent>,
        private authService: AuthenticationService,
        private languageService: LanguageService,
        private userService: Api.UsersService,
        private retailersService: Api.RetailersService,
        private logger: LoggerService,
        private formDataReader: Utilities.FormDataReader,
        private formBuilder: UntypedFormBuilder,
        @Inject(MAT_DIALOG_DATA) public data?: Entities.User) {
        this.log = logger.getLogFn('UserDetailsEditComponent');
        this.logError = logger.getLogFn('UserDetailsEditComponent', 'error');
        this.logSuccess = logger.getLogFn('UserDetailsEditComponent', 'success');
    }

    ngOnInit() {

        this.colors = _.sortBy(this.colors, color => color.viewValue);

        this.currentUser = this.authService.getCurrentUser();

        // check to see if we had a user passed via dialog data
        if (this.data) {
            this.user = this.data;
        }

        this.isNew = !this.user;

        if (this.isNew) {
            this.user = <Entities.User>{};
        }

        // Details controls
        this.userName = new UntypedFormControl(
            { value: this.user ? this.user.userName : null, disabled: !this.isNew }, 
            [Validators.required]
        );
        this.firstName = new UntypedFormControl(this.user ? this.user.firstName : null, [Validators.required]);
        this.lastName = new UntypedFormControl(this.user ? this.user.lastName : null, [Validators.required]);
        this.password = new UntypedFormControl(null, Validators.compose([
            CustomValidators.patternValidator(/\d/, { hasNumber: true }),
            CustomValidators.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
            CustomValidators.patternValidator(/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, { hasSpecial: true }),
            Validators.minLength(8)
        ]));
        this.confirmPassword = new UntypedFormControl();
        this.roles = new UntypedFormControl(this.user ? this.user.roles : [], []);
        this.retailerId = new UntypedFormControl(this.user ? this.user.retailerId : null);

        // Change Password controls
        this.changePassword = new UntypedFormControl('', Validators.compose([
            CustomValidators.patternValidator(/\d/, { hasNumber: true }),
            CustomValidators.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
            CustomValidators.patternValidator(/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, { hasSpecial: true }),
            Validators.minLength(8)
        ]));
        this.confirmChangePassword = new UntypedFormControl('');

        this.userDetails = this.formBuilder.group({
            userName: this.userName,
            firstName: this.firstName,
            lastName: this.lastName,
            password: this.password,
            confirmPassword: this.confirmPassword,
            roles: this.roles,
            retailerId: this.retailerId,
            changePassword: this.changePassword,
            confirmChangePassword: this.confirmChangePassword
        }, {
            validators: [
                MustMatch('password', 'confirmPassword'),
                MustMatch('changePassword', 'confirmChangePassword', true)
            ]
        });
    }

    // convenience getter for easy access to form fields
    get f() { return this.userDetails.controls; }

    isRetailerRequired(): boolean {
        const updatedUser = <Entities.User>this.userDetails.value;
        let rolesToCheck: string[] = [this.enums.role.retailerManager, this.enums.role.retailerRepresentative];
        if (updatedUser.roles) {
            const roleNames: string[] = updatedUser.roles.map(r => r.name);
            if (roleNames.some(role => rolesToCheck.includes(role)))
                return true;
        }
        return false;
    }

    cancel(): void {
        this.activeModal.close(false);
    }

    clickAddFile(): void {
        document.getElementById('fileInput').click();
    }

    compareRoles(r1: Entities.Role, r2: Entities.Role) {
        return r1 && r2 && r1.roleId === r2.roleId;
    }

    singleFileSelected(event: any): void {
        this.showCropperCard = true;
        this.imageChangedEvent = event;
    }

    imageCropped(event: ImageCroppedEvent) {
        this.croppedImage = event.base64;
    }

    imageLoaded() {
        this.showCropper = true;
    }

    loadImageFailed() {
        this.logError('Loading image failed. Try again.', null, true);
    }

    getTitle() {
        if (this.isNew) {
            return 'New User';
        } else if (this.user && this.user.userId === this.currentUser.userId) {
            return 'My Settings';
        } else {
            return `Edit ${this.user.firstName} ${this.user.lastName}`;
        }
    }

    async save() {
        this.loading = true;
        console.log(JSON.stringify(this.userDetails.value));
      
        const updatedUser = <Entities.User>this.userDetails.value;
        updatedUser.userId = this.user.userId;
      
        try {
          let result: any;
      
          if (this.isNew) {
            result = await this.userService.createUser(updatedUser);
          } else {
            result = await this.userService.updateUser(updatedUser);
          }
      
          if (result) {
            this.activeModal.close(result);
          }
        } catch (error) {
          console.error('Save failed:', error);
        } finally {
          this.loading = false;
        }
      }
}
